import { useEffect, useMemo, useState, useCallback } from 'react';
import { crmStore, useCrm } from '../store';
import { Card, PageTitle, StatutBadge } from '../ui';
import { formatEUR, formatDateFR } from '../utils';
import { Mic2, ClipboardList, Calculator, Save, MapPin, Loader2, Check, ArrowUpDown, ArrowUp, ArrowDown, Compass, FileText, Send, Trash2, Calendar, Link2, Sparkles, ExternalLink, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { searchAddress, calcRoute, formatDuree, haversineKm, geocodeAddress, type NominatimSuggestion, type Coords } from '../utils/geo';
import { withDemoFlag, demoFilterValue } from '../demoMode';
import { useConfirm } from '../components/ConfirmDialog';
import { TvaSelect } from '../components/TvaSelect';

type SortKey = 'name' | 'client' | 'dateEvent' | 'dateOuverture' | 'count' | 'ca';
type SortDir = 'asc' | 'desc';

const CALC_STORAGE_KEY = 'cygnature-calcul-draft-v1';
const CALC_PREFILL_KEY = 'cygnature-calc-prefill-v1';

interface ArtisteOption {
  id: string;
  nom_scene: string;
  domaine_principal: string | null;
  tarif_base: number | null;
  frais_deplacement: number | null;
  tva_pct: number | null;
  lat: number | null;
  lng: number | null;
  ville: string | null;
}

interface BrouillonRow {
  id: string;
  created_at: string;
  client_nom: string | null;
  date_event: string | null;
  lieu: string | null;
  artiste_nom: string | null;
  artiste_id: string | null;
  domaine: string | null;
  tarif_ht: number | null;
  tva_pct: number | null;
  marge_pct: number | null;
  frais: number | null;
  km: number | null;
  duree_trajet: string | null;
  prop_ttc: number | null;
  note: string | null;
  prestation_id: string | null;
  converti_le: string | null;
  lieu_lat: number | null;
  lieu_lng: number | null;
  option1_libelle?: string | null; option1_ht?: number | null;
  option2_libelle?: string | null; option2_ht?: number | null;
  option3_libelle?: string | null; option3_ht?: number | null;
  option4_libelle?: string | null; option4_ht?: number | null;
  frais_vhr_deplacement?: number | null;
  frais_vhr_hotel?: number | null;
  frais_vhr_production?: number | null;
}

export default function ArtistesCalcul() {
  const data = useCrm();
  const confirm = useConfirm();
  const [tab, setTab] = useState<'liste' | 'calc' | 'recherche' | 'brouillons'>('liste');
  const [artistName, setArtistName] = useState('');
  const [domainName, setDomainName] = useState(data.domaines[0] ?? '');
  const [tarifHT, setTarifHT] = useState('0');
  const [tva, setTva] = useState('20');
  const [marge, setMarge] = useState('30');
  const [frais, setFrais] = useState('0');
  const [km, setKm] = useState('0');
  const [propTTC, setPropTTC] = useState('');
  const [clientNom, setClientNom] = useState('');
  const [dateEvent, setDateEvent] = useState('');
  const [linkedPrestationId, setLinkedPrestationId] = useState<string | null>(null);
  // Mode du calculateur : null = pas de choix, 'libre' = saisie libre, 'lie' = lié à une prestation
  const [calcMode, setCalcMode] = useState<'libre' | 'lie' | null>(null);
  // Contexte hérité (lecture seule en mode lié, juste informatif en mode libre)
  const [ctxLibelle, setCtxLibelle] = useState('');
  const [ctxDemande, setCtxDemande] = useState('');
  const [ctxRemarques, setCtxRemarques] = useState('');
  const [ctxDomaines, setCtxDomaines] = useState<string[]>([]);

  // Options multiples (jusqu'à 4) - alignées sur Excel client
  const [opt1Lib, setOpt1Lib] = useState(''); const [opt1HT, setOpt1HT] = useState('');
  const [opt2Lib, setOpt2Lib] = useState(''); const [opt2HT, setOpt2HT] = useState('');
  const [opt3Lib, setOpt3Lib] = useState(''); const [opt3HT, setOpt3HT] = useState('');
  const [opt4Lib, setOpt4Lib] = useState(''); const [opt4HT, setOpt4HT] = useState('');

  // Frais VHR détaillés
  const [vhrDepl, setVhrDepl] = useState('0');
  const [vhrHotel, setVhrHotel] = useState('0');
  const [vhrProd, setVhrProd] = useState('0');

  // Lignes artistes ajoutées au calcul (panier multi-artistes)
  type LigneArtiste = {
    id: string;
    artiste_id: string | null;
    artiste_nom: string;
    domaine: string | null;
    tarif_ht: number;
    options_ht: number;
    options?: { libelle: string; ht: number }[];
    km: number | null;
    duree_trajet: string | null;
  };
  const [lignes, setLignes] = useState<LigneArtiste[]>([]);

  const buildOptionsFromFields = () => [
    { libelle: opt1Lib.trim(), ht: Number(opt1HT) || 0 },
    { libelle: opt2Lib.trim(), ht: Number(opt2HT) || 0 },
    { libelle: opt3Lib.trim(), ht: Number(opt3HT) || 0 },
    { libelle: opt4Lib.trim(), ht: Number(opt4HT) || 0 },
  ].filter((o) => o.libelle && o.ht > 0);

  const buildCalcDataFromLine = (l: LigneArtiste) => {
    const opts = l.options || [];
    return {
      tarifHT: l.tarif_ht,
      margePct: Number(marge) || 0,
      fraisDepl: 0,
      tvaPct: Number(tva) || 20,
      vhrDepl: 0,
      vhrHotel: 0,
      vhrProd: 0,
      opt1Lib: opts[0]?.libelle || '', opt1HT: opts[0]?.ht || 0, opt1On: !!opts[0], opt1Tarif: opts[0]?.ht || 0, opt1Marge: 0, opt1Note: '',
      opt2Lib: opts[1]?.libelle || '', opt2HT: opts[1]?.ht || 0, opt2On: !!opts[1], opt2Tarif: opts[1]?.ht || 0, opt2Marge: 0, opt2Note: '',
      opt3Lib: opts[2]?.libelle || '', opt3HT: opts[2]?.ht || 0, opt3On: !!opts[2], opt3Tarif: opts[2]?.ht || 0, opt3Marge: 0, opt3Note: '',
      opt4Lib: opts[3]?.libelle || '', opt4HT: opts[3]?.ht || 0, opt4On: !!opts[3], opt4Tarif: opts[3]?.ht || 0, opt4Marge: 0, opt4Note: '',
      prixLibreHT: 0,
    };
  };


  // Préfill depuis "Calculer le tarif" sur PrestationForm
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('prefill') !== '1') return;
    try {
      const raw = sessionStorage.getItem(CALC_PREFILL_KEY);
      if (!raw) return;
      const p = JSON.parse(raw);
      sessionStorage.removeItem(CALC_PREFILL_KEY);
      // Si la prestation complète est fournie, on la charge avec ses artistes/coûts
      if (p.prestation && p.prestation.id) {
        setTab('calc');
        loadFromPrestation(p.prestation);
        return;
      }
      // Sinon, fallback : pré-remplit juste le contexte
      if (p.prestationId) setLinkedPrestationId(p.prestationId);
      if (p.clientNom) setClientNom(p.clientNom);
      if (p.dateEvent) setDateEvent(p.dateEvent);
      if (p.lieu) setLieuPrestation(p.lieu);
      if (p.domaine) setDomainName(p.domaine);
      if (p.km != null) setKm(String(p.km));
      setTab('calc');
      setCalcMode(p.prestationId ? 'lie' : 'libre');
      toast.success(p.prestationId ? 'Calcul lié à la prestation' : 'Données pré-remplies');
    } catch (e) { console.error('[prefill]', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lot 1B : artiste sélectionné depuis Supabase + lieu de prestation
  const [artistesDb, setArtistesDb] = useState<ArtisteOption[]>([]);
  const [selectedArtisteId, setSelectedArtisteId] = useState<string>('');
  const [lieuPrestation, setLieuPrestation] = useState('');
  const [lieuCoords, setLieuCoords] = useState<Coords | null>(null);
  const [dureeTrajet, setDureeTrajet] = useState<string>('');
  const [computingRoute, setComputingRoute] = useState(false);

  // Recherche par distance + brouillons
  const [searchLieu, setSearchLieu] = useState('');
  const [searchCoords, setSearchCoords] = useState<Coords | null>(null);
  const [searchDate, setSearchDate] = useState('');
  const [searchDomaine, setSearchDomaine] = useState('');
  const [searchResults, setSearchResults] = useState<Array<ArtisteOption & { km: number; minutes: number }>>([]);
  const [searching, setSearching] = useState(false);
  const [brouillons, setBrouillons] = useState<BrouillonRow[]>([]);
  const [topProches, setTopProches] = useState<Array<ArtisteOption & { km: number; minutes: number }>>([]);

  // Charge la liste réelle des artistes depuis Supabase
  useEffect(() => {
    (async () => {
      const { data: rows, error } = await supabase
        .from('artistes')
        .select('id, nom_scene, domaine_principal, tarif_base, frais_deplacement, tva_pct, lat, lng, ville')
        .is('deleted_at', null)
        .order('nom_scene');
      if (!error && rows) setArtistesDb(rows as ArtisteOption[]);
    })();
  }, []);

  const selectedArtiste = useMemo(
    () => artistesDb.find((a) => a.id === selectedArtisteId) || null,
    [artistesDb, selectedArtisteId]
  );

  // Quand on choisit un artiste : préremplit nom, domaine, tarif, TVA, frais
  useEffect(() => {
    if (!selectedArtiste) return;
    setArtistName(selectedArtiste.nom_scene);
    if (selectedArtiste.domaine_principal) setDomainName(selectedArtiste.domaine_principal);
    if (selectedArtiste.tarif_base != null) setTarifHT(String(selectedArtiste.tarif_base));
    if (selectedArtiste.tva_pct != null) setTva(String(selectedArtiste.tva_pct));
    if (selectedArtiste.frais_deplacement != null) setFrais(String(selectedArtiste.frais_deplacement));
  }, [selectedArtiste]);

  // Quand artiste + lieu coords sont définis → calcule la distance routière artiste → lieu
  useEffect(() => {
    if (!selectedArtiste?.lat || !selectedArtiste?.lng || !lieuCoords) return;
    setComputingRoute(true);
    calcRoute(
      { lat: Number(selectedArtiste.lat), lon: Number(selectedArtiste.lng) },
      lieuCoords
    )
      .then((r) => {
        setKm(String(r.km));
        setDureeTrajet(formatDuree(r.minutes));
        if (r.source === 'haversine') toast.info('Trajet routier indisponible — distance approximative');
      })
      .catch(() => toast.error('Calcul du trajet impossible'))
      .finally(() => setComputingRoute(false));
  }, [selectedArtiste, lieuCoords]);

  const loadBrouillons = async () => {
    const { data: rows, error } = await supabase
      .from('calculs_brouillons')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && rows) setBrouillons(rows as BrouillonRow[]);
  };
  useEffect(() => { loadBrouillons(); }, []);

  useEffect(() => {
    if (!lieuCoords) { setTopProches([]); return; }
    const geocoded = artistesDb.filter((a) => a.lat != null && a.lng != null);
    const withDist = geocoded.map((a) => {
      const km = haversineKm({ lat: Number(a.lat), lon: Number(a.lng) }, lieuCoords);
      return { ...a, km: Math.round(km), minutes: Math.round((km / 70) * 60) };
    });
    withDist.sort((a, b) => a.km - b.km);
    setTopProches(withDist.slice(0, 5));
  }, [lieuCoords, artistesDb]);

  const runSearch = async () => {
    if (!searchCoords) { toast.error('Choisissez un lieu d\'événement'); return; }
    setSearching(true);
    try {
      let pool = artistesDb.filter((a) => a.lat != null && a.lng != null);
      if (searchDomaine) pool = pool.filter((a) => (a.domaine_principal || '').toLowerCase() === searchDomaine.toLowerCase());
      const limited = pool.slice(0, 50);
      const results = await Promise.all(
        limited.map(async (a) => {
          try {
            const r = await calcRoute({ lat: Number(a.lat), lon: Number(a.lng) }, searchCoords);
            return { ...a, km: r.km, minutes: r.minutes };
          } catch {
            const km = haversineKm({ lat: Number(a.lat), lon: Number(a.lng) }, searchCoords);
            return { ...a, km: Math.round(km), minutes: Math.round((km / 70) * 60) };
          }
        })
      );
      results.sort((a, b) => a.km - b.km);
      setSearchResults(results);
      toast.success(`${results.length} artiste(s) classé(s) par proximité`);
    } finally {
      setSearching(false);
    }
  };

  const useArtisteForCalc = (a: ArtisteOption & { km?: number; minutes?: number }) => {
    setSelectedArtisteId(a.id);
    if (searchLieu && searchCoords) {
      setLieuPrestation(searchLieu);
      setLieuCoords(searchCoords);
    }
    if (searchDate) setDateEvent(searchDate);
    if (a.km != null) setKm(String(a.km));
    if (a.minutes != null) setDureeTrajet(formatDuree(a.minutes));
    setTab('calc');
  };

  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir(k === 'name' || k === 'client' ? 'asc' : 'desc'); }
  };

  const artistRows = useMemo(() => {
    const names = Array.from(new Set([...data.artistes, ...data.prest.flatMap((p) => p.artistes)].filter(Boolean))).sort((a, b) => a.localeCompare(b));
    const rows = names.map((name) => {
      const prestations = data.prest.filter((p) => p.artistes.includes(name));
      const sortedByEvent = [...prestations].sort((a, b) => b.dateEvent.localeCompare(a.dateEvent));
      const latest = sortedByEvent[0];
      const sortedByCreation = [...prestations].sort((a, b) => (a.dateCreation || '').localeCompare(b.dateCreation || ''));
      const dateOuverture = sortedByCreation[0]?.dateCreation || '';
      const signedCA = prestations.filter((p) => p.statut === 'Signé').reduce((s, p) => s + (p.propTTC ?? 0), 0);
      return { name, prestations, latest, dateOuverture, signedCA };
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      switch (sortKey) {
        case 'name': return a.name.localeCompare(b.name) * dir;
        case 'client': return ((a.latest?.client || '').localeCompare(b.latest?.client || '')) * dir;
        case 'dateEvent': return ((a.latest?.dateEvent || '').localeCompare(b.latest?.dateEvent || '')) * dir;
        case 'dateOuverture': return (a.dateOuverture.localeCompare(b.dateOuverture)) * dir;
        case 'count': return (a.prestations.length - b.prestations.length) * dir;
        case 'ca': return (a.signedCA - b.signedCA) * dir;
        default: return 0;
      }
    });
    return rows;
  }, [data.artistes, data.prest, sortKey, sortDir]);

  const handleRowClick = (name: string) => {
    const match = artistesDb.find((a) => a.nom_scene === name);
    if (match) setSelectedArtisteId(match.id);
    else setArtistName(name);
    setTab('calc');
    if (!calcMode) setCalcMode('libre');
  };

  // Charge tout le contexte d'une prestation existante dans le calculateur
  const loadFromPrestation = async (p: import('./../types').Prestation) => {
    // p.id peut être un numero (ex: 261119/THOMAS/LEON/001) — résoudre l'UUID DB réel
    let realId = p.id;
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(p.id)) {
      const { data: prow } = await supabase
        .from('prestations')
        .select('id')
        .eq('numero', p.id)
        .maybeSingle();
      if (prow?.id) realId = prow.id;
      else { toast.error('Prestation introuvable en base'); return; }
    }
    setLinkedPrestationId(realId);
    setClientNom(p.client || '');
    setDateEvent(p.dateEvent || '');
    setLieuPrestation(p.lieu || '');
    // Géocode le lieu pour permettre le calcul automatique de la distance artiste → lieu
    if (p.lieu) {
      geocodeAddress(p.lieu)
        .then((g) => { if (g) setLieuCoords({ lat: g.lat, lon: g.lon }); else setLieuCoords(null); })
        .catch(() => setLieuCoords(null));
    } else {
      setLieuCoords(null);
    }
    setCtxLibelle(p.libelle || '');
    setCtxDemande(p.demande || '');
    setCtxRemarques(p.remarques || '');
    setCtxDomaines(p.domaines || []);
    if (p.domaines?.[0]) setDomainName(p.domaines[0]);
    if (p.km != null) setKm(String(p.km));
    if (p.propTTC != null) setPropTTC(String(p.propTTC));
    setCalcMode('lie');

    // Récupère les artistes déjà liés à la prestation
    const { data: rows } = await supabase
      .from('prestations_artistes')
      .select('id, artiste_id, artiste_nom, cout, prop_ttc, marge_pct, km, duree_trajet, notes')
      .eq('prestation_id', realId);

    if (rows && rows.length > 0) {
      // Premier artiste → champs principaux du calculateur
      const first: any = rows[0];
      if (first.artiste_id) setSelectedArtisteId(first.artiste_id);
      else setArtistName(first.artiste_nom || '');
      if (first.cout != null) setTarifHT(String(first.cout));
      if (first.marge_pct != null) setMarge(String(first.marge_pct));
      if (first.prop_ttc != null) setPropTTC(String(first.prop_ttc));
      if (first.km != null) setKm(String(first.km));

      // Artistes suivants → lignes additionnelles
      const extras = rows.slice(1).map((r: any) => ({
        id: r.id,
        artiste_id: r.artiste_id,
        artiste_nom: r.artiste_nom || '',
        domaine: '',
        tarif_ht: Number(r.cout || 0),
        options_ht: 0,
        marge_pct: Number(r.marge_pct || 30),
        km: r.km != null ? Number(r.km) : null,
        duree_trajet: r.duree_trajet || null,
      }));
      if (extras.length) setLignes(extras as any);
      toast.success(`Prestation chargée — ${rows.length} artiste(s) récupéré(s)`);
    } else {
      toast.success('Prestation chargée — partie artistique à renseigner');
    }
  };

  // Réinitialise le mode et tout le contexte (retour au choix initial)
  const resetCalcMode = () => {
    setCalcMode(null);
    setLinkedPrestationId(null);
    setClientNom('');
    setDateEvent('');
    setLieuPrestation('');
    setLieuCoords(null);
    setCtxLibelle(''); setCtxDemande(''); setCtxRemarques(''); setCtxDomaines([]);
    setLignes([]);
    setPropTTC('');
    setFrais('0'); setVhrDepl('0'); setVhrHotel('0'); setVhrProd('0');
  };

  const calc = useMemo(() => {
    const ht = Number(tarifHT) || 0;
    const margePct = Number(marge) || 0;
    const fraisLegacy = Number(frais) || 0;
    const optionsHT =
      (Number(opt1HT) || 0) + (Number(opt2HT) || 0) +
      (Number(opt3HT) || 0) + (Number(opt4HT) || 0);
    const vhrTotal = (Number(vhrDepl) || 0) + (Number(vhrHotel) || 0) + (Number(vhrProd) || 0);
    const fraisTotal = fraisLegacy + vhrTotal;
    const lignesHT = lignes.reduce((s, l) => s + (l.tarif_ht || 0) + (l.options_ht || 0), 0);
    const mainHT = ht + optionsHT + lignesHT; // Tous les artistes (courant + ajoutés)
    const artisteHT = mainHT;
    const coutTotal = artisteHT + fraisTotal;
    const suggestion = margePct >= 100 ? coutTotal : coutTotal / (1 - margePct / 100);
    const clientHT = Number(propTTC) || suggestion;
    const gain = clientHT - coutTotal;
    return {
      mainHT, optionsHT, vhrTotal, lignesHT,
      artisteHT, coutTotal, suggestion, clientHT, gain,
      margeReelle: clientHT > 0 ? (gain / clientHT) * 100 : 0,
    };
  }, [tarifHT, marge, frais, propTTC, opt1HT, opt2HT, opt3HT, opt4HT, vhrDepl, vhrHotel, vhrProd, lignes]);



  const handleAddArtist = () => {
    const name = artistName.trim();
    if (!name) {
      toast.error('Renseignez le nom de l’artiste');
      return;
    }
    if (data.artistes.includes(name)) {
      toast.error('Cet artiste existe déjà');
      return;
    }
    crmStore.update({ artistes: [...data.artistes, name].sort((a, b) => a.localeCompare(b)) });
    toast.success(`${name} ajouté au CRM`);
    setTab('liste');
  };

  const resetArtisteFields = () => {
    setSelectedArtisteId('');
    setArtistName('');
    setDomainName(data.domaines[0] ?? '');
    setTarifHT('0');
    setKm('0');
    setDureeTrajet('');
    setOpt1Lib(''); setOpt1HT('');
    setOpt2Lib(''); setOpt2HT('');
    setOpt3Lib(''); setOpt3HT('');
    setOpt4Lib(''); setOpt4HT('');
  };

  const handleAddLigne = () => {
    if (!artistName.trim()) { toast.error("Renseignez l'artiste à ajouter"); return; }
    const optionsHT =
      (Number(opt1HT) || 0) + (Number(opt2HT) || 0) +
      (Number(opt3HT) || 0) + (Number(opt4HT) || 0);
    const options = buildOptionsFromFields();
    const ligne: LigneArtiste = {
      id: crypto.randomUUID(),
      artiste_id: selectedArtisteId || null,
      artiste_nom: artistName.trim(),
      domaine: domainName || null,
      tarif_ht: Number(tarifHT) || 0,
      options_ht: optionsHT,
      options,
      km: Number(km) || null,
      duree_trajet: dureeTrajet || null,
    };
    setLignes((arr) => [...arr, ligne]);
    resetArtisteFields();
    toast.success(`${ligne.artiste_nom} ajouté au calcul`);
  };

  const handleRemoveLigne = (id: string) => {
    setLignes((arr) => arr.filter((l) => l.id !== id));
  };

  const handleValider = async () => {
    if (!clientNom.trim()) { toast.error('Renseignez le client'); return; }
    // Toutes les lignes : celles déjà ajoutées + l'artiste courant si rempli
    const all: LigneArtiste[] = [...lignes];
    if (artistName.trim() && (Number(tarifHT) > 0 || lignes.length === 0)) {
      const optionsHT =
        (Number(opt1HT) || 0) + (Number(opt2HT) || 0) +
        (Number(opt3HT) || 0) + (Number(opt4HT) || 0);
      const options = buildOptionsFromFields();
      all.push({
        id: crypto.randomUUID(),
        artiste_id: selectedArtisteId || null,
        artiste_nom: artistName.trim(),
        domaine: domainName || null,
        tarif_ht: Number(tarifHT) || 0,
        options_ht: optionsHT,
        options,
        km: Number(km) || null,
        duree_trajet: dureeTrajet || null,
      });
    }
    if (all.length === 0) { toast.error('Ajoutez au moins un artiste'); return; }

    let prestId = linkedPrestationId;

    if (prestId) {
      // Mode lié : met à jour la prestation existante et remplace ses artistes
      await supabase
        .from('prestations')
        .update({
          prop_ttc: calc.clientHT,
          km: all[0]?.km ?? null,
        })
        .eq('id', prestId);

      // Remplace les artistes liés (delete + reinsert)
      await supabase.from('prestations_artistes').delete().eq('prestation_id', prestId);
    } else {
      // Crée une nouvelle prestation
      const domainesUnique = Array.from(new Set(all.map((l) => l.domaine).filter(Boolean) as string[]));
      const { data: pres, error: e1 } = await supabase
        .from('prestations')
        .insert(withDemoFlag({
          client_nom: clientNom.trim(),
          date_event: dateEvent || null,
          lieu: lieuPrestation || null,
          domaines: domainesUnique,
          prop_ttc: calc.clientHT,
          km: all[0]?.km ?? null,
          statut: 'Propal A FAIRE' as any,
        }))
        .select('id')
        .single();
      if (e1 || !pres) { toast.error(`Erreur prestation : ${e1?.message ?? 'inconnue'}`); return; }
      prestId = pres.id;
    }

    // Insère les artistes liés
    const { error: e2 } = await supabase.from('prestations_artistes').insert(
      all.map((l) => withDemoFlag({
        prestation_id: prestId,
        artiste_id: l.artiste_id,
        artiste_nom: l.artiste_nom,
        cout: l.tarif_ht + l.options_ht,
        marge_pct: Number(marge) || 0,
        km: l.km,
        duree_trajet: l.duree_trajet,
        notes: (l.options || []).map((o) => `${o.libelle}: ${Math.round(o.ht)}€`).join(' · ') || null,
        calc_data: buildCalcDataFromLine(l) as any,
        statut: 'option',
      })) as any
    );
    if (e2) { toast.error(`Erreur artistes : ${e2.message}`); return; }

    toast.success(linkedPrestationId
      ? `Prestation mise à jour avec ${all.length} artiste(s)`
      : `Prestation créée avec ${all.length} artiste(s)`);

    // Reset
    setLignes([]);
    resetArtisteFields();
    if (!linkedPrestationId) {
      setClientNom('');
      setDateEvent('');
      setLieuPrestation('');
      setLieuCoords(null);
      setPropTTC('');
      setFrais('0'); setVhrDepl('0'); setVhrHotel('0'); setVhrProd('0');
    }
  };

  const handleConvertirBrouillon = async (b: BrouillonRow) => {
    if (b.prestation_id) { toast.info('Déjà converti en prestation'); return; }
    if (!b.client_nom?.trim()) { toast.error('Le brouillon doit avoir un nom de client'); return; }
    const { data: pres, error: e1 } = await supabase
      .from('prestations')
      .insert(withDemoFlag({
        client_nom: b.client_nom,
        date_event: b.date_event,
        lieu: b.lieu,
        domaines: b.domaine ? [b.domaine] : [],
        prop_ttc: b.prop_ttc,
        km: b.km,
        statut: 'Propal A FAIRE' as any,
      }))
      .select('id')
      .single();
    if (e1 || !pres) { toast.error(`Erreur prestation : ${e1?.message ?? 'inconnue'}`); return; }
    if (b.artiste_id || b.artiste_nom) {
      await supabase.from('prestations_artistes').insert(withDemoFlag({
        prestation_id: pres.id,
        artiste_id: b.artiste_id,
        artiste_nom: b.artiste_nom,
        cout: b.tarif_ht,
        prop_ttc: b.prop_ttc,
        marge_pct: b.marge_pct,
        km: b.km,
        duree_trajet: b.duree_trajet,
        statut: 'option',
      }) as any);
    }
    await supabase
      .from('calculs_brouillons')
      .update({ prestation_id: pres.id, converti_le: new Date().toISOString() })
      .eq('id', b.id);
    toast.success('Brouillon converti en proposition');
    loadBrouillons();
  };

  const handleDeleteBrouillon = async (id: string) => {
    const ok = await confirm({ title: 'Supprimer ce brouillon ?', confirmLabel: 'Supprimer', destructive: true });
    if (!ok) return;
    const { error } = await supabase.from('calculs_brouillons').delete().eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Brouillon supprimé');
    loadBrouillons();
  };

  return (
    <div>
      <PageTitle title="Artistes & Calculateur" subtitle="Calcul de prestation et marge Cygnature" icon={<Mic2 className="text-amber-400" size={24} strokeWidth={1.5} />} action={
        <button onClick={handleAddArtist} className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-medium rounded-lg text-sm transition-colors">+ Nouvel Artiste</button>
      } />

      <div className="flex gap-1 border-b border-slate-800 mb-6">
        <button onClick={() => setTab('liste')} className={`px-4 py-2.5 text-sm border-b-2 transition-colors ${tab === 'liste' ? 'border-amber-400 text-amber-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
          <ClipboardList size={14} className="inline mr-2" strokeWidth={1.5} /> Liste Artistes
        </button>
        <button onClick={() => setTab('calc')} className={`px-4 py-2.5 text-sm border-b-2 transition-colors ${tab === 'calc' ? 'border-amber-400 text-amber-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
          <Calculator size={14} className="inline mr-2" strokeWidth={1.5} /> Calculateur Prestation
        </button>
        <button onClick={() => setTab('recherche')} className={`px-4 py-2.5 text-sm border-b-2 transition-colors ${tab === 'recherche' ? 'border-amber-400 text-amber-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
          <Compass size={14} className="inline mr-2" strokeWidth={1.5} /> Recherche par distance
        </button>
        <button onClick={() => setTab('brouillons')} className={`px-4 py-2.5 text-sm border-b-2 transition-colors ${tab === 'brouillons' ? 'border-amber-400 text-amber-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
          <FileText size={14} className="inline mr-2" strokeWidth={1.5} /> Brouillons {brouillons.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px]">{brouillons.length}</span>}
        </button>
      </div>

      {tab === 'liste' && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/80 text-[10px] text-slate-400 uppercase tracking-wider">
                <tr>
                  <SortableTh label="Artiste" k="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Dernier client" k="client" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Date événement" k="dateEvent" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Ouverture dossier" k="dateOuverture" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Prestations" k="count" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <SortableTh label="CA signé" k="ca" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <th className="px-3 py-3 text-left">Statut</th>
                </tr>
              </thead>
              <tbody>
                {artistRows.map((row) => (
                  <tr
                    key={row.name}
                    onClick={() => handleRowClick(row.name)}
                    className="border-t border-slate-800/60 hover:bg-amber-500/5 cursor-pointer transition-colors"
                    title="Cliquer pour ouvrir dans le calculateur"
                  >
                    <td className="px-3 py-3 text-amber-300 font-medium text-xs">{row.name}</td>
                    <td className="px-3 py-3 text-slate-300 text-xs">{row.latest?.client ?? '—'}</td>
                    <td className="px-3 py-3 text-slate-400 text-xs">{row.latest ? formatDateFR(row.latest.dateEvent) : '—'}</td>
                    <td className="px-3 py-3 text-slate-400 text-xs">{row.dateOuverture ? formatDateFR(row.dateOuverture) : '—'}</td>
                    <td className="px-3 py-3 text-right text-slate-300 text-xs">{row.prestations.length}</td>
                    <td className="px-3 py-3 text-right text-amber-400 text-xs">{formatEUR(row.signedCA)}</td>
                    <td className="px-3 py-3">{row.latest ? <StatutBadge statut={row.latest.statut} /> : <span className="text-xs text-slate-500">Catalogué</span>}</td>
                  </tr>
                ))}
                {!artistRows.length && (
                  <tr><td colSpan={7} className="text-center py-16 text-slate-500">
                    <Mic2 size={32} className="mx-auto mb-3 text-slate-600" strokeWidth={1.5} />
                    Aucun artiste — utilisez le calculateur pour en ajouter un.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'calc' && (
        <div className="grid grid-cols-1 xl:grid-cols-[1.25fr_0.75fr] gap-6">
          <Card className="p-8">
            {/* Choix du mode au démarrage */}
            {calcMode === null && (
              <div>
                <h3 className="text-lg font-heading text-slate-200 mb-2">Calculateur de prestation</h3>
                <p className="text-xs text-slate-400 mb-6">Choisissez comment démarrer le calcul.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => setCalcMode('lie')}
                    className="text-left p-5 rounded-xl border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 transition-colors"
                  >
                    <div className="flex items-center gap-2 text-amber-300 font-medium mb-1">
                      <Link2 size={16} strokeWidth={1.5} /> Lier à une prestation existante
                    </div>
                    <div className="text-[11px] text-slate-400 leading-relaxed">
                      Recharge automatiquement client, date, lieu, libellé et demande depuis la fiche.
                      Vous ne saisissez que la partie artistique.
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCalcMode('libre')}
                    className="text-left p-5 rounded-xl border border-slate-700 bg-slate-900/40 hover:border-slate-500 transition-colors"
                  >
                    <div className="flex items-center gap-2 text-slate-200 font-medium mb-1">
                      <Sparkles size={16} strokeWidth={1.5} /> Calcul libre (sans fiche)
                    </div>
                    <div className="text-[11px] text-slate-400 leading-relaxed">
                      Saisie manuelle complète — utile pour une simulation rapide ou un client non encore créé.
                      Une nouvelle prestation sera créée à la validation.
                    </div>
                  </button>
                </div>

                {/* Sélecteur direct si on choisit "lié" */}
                <div className="pt-5 border-t border-slate-800">
                  <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">
                    Ou sélectionnez directement une prestation
                  </div>
                  <PrestationPicker
                    prestations={data.prest}
                    onPick={loadFromPrestation}
                  />
                </div>
              </div>
            )}

            {calcMode !== null && (
              <>
                <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                  <h3 className="text-lg font-heading text-slate-200 flex items-center gap-2">
                    Calculateur de prestation
                    {calcMode === 'lie' && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                        <Link2 size={10} strokeWidth={1.5} /> Lié à une fiche
                      </span>
                    )}
                  </h3>
                  <button
                    type="button"
                    onClick={resetCalcMode}
                    className="text-[11px] text-slate-400 hover:text-amber-300 flex items-center gap-1"
                  >
                    <ArrowLeft size={12} strokeWidth={1.5} /> Changer de mode
                  </button>
                </div>

                {calcMode === 'lie' && !linkedPrestationId && (
                  <div className="mb-5">
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">
                      Sélectionnez la prestation à charger
                    </div>
                    <PrestationPicker
                      prestations={data.prest}
                      onPick={loadFromPrestation}
                    />
                  </div>
                )}

                {calcMode === 'lie' && linkedPrestationId && (
                  <div className="mb-5 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="flex items-center gap-2 font-medium">
                        <Link2 size={14} strokeWidth={1.5} />
                        Prestation chargée — partie artistique à compléter ci-dessous
                      </span>
                      <Link
                        to={`/crm-pro-evenementiel/prestations/${linkedPrestationId}`}
                        className="text-[10px] text-amber-300 hover:text-amber-200 flex items-center gap-1"
                      >
                        Voir la fiche <ExternalLink size={10} strokeWidth={1.5} />
                      </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-[11px] text-slate-300">
                      <div><span className="text-slate-500">Client : </span>{clientNom || '—'}</div>
                      <div><span className="text-slate-500">Date : </span>{dateEvent ? formatDateFR(dateEvent) : '—'}</div>
                      <div className="md:col-span-1"><span className="text-slate-500">Lieu : </span>{lieuPrestation || '—'}</div>
                      {ctxLibelle && <div><span className="text-slate-500">Type : </span>{ctxLibelle}</div>}
                      {ctxDemande && <div><span className="text-slate-500">Demande : </span>{ctxDemande}</div>}
                      {ctxDomaines.length > 0 && <div><span className="text-slate-500">Domaines : </span>{ctxDomaines.join(', ')}</div>}
                    </div>
                    {ctxRemarques && (
                      <div className="mt-2 pt-2 border-t border-amber-500/20 text-[11px] text-slate-400 italic">
                        « {ctxRemarques} »
                      </div>
                    )}
                  </div>
                )}

                {/* Mode libre : saisie client + date */}
                {calcMode === 'libre' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 pb-5 border-b border-slate-800">
                    <Field label="Client">
                      <ClientCalcPicker
                        value={clientNom}
                        onChange={setClientNom}
                        prestations={data.prest}
                        onPickPrestation={(p) => loadFromPrestation(p)}
                      />
                    </Field>
                    <Field label="Date événement">
                      <div className="relative">
                        <input type="date" value={dateEvent} onChange={(e) => setDateEvent(e.target.value)} className={inputCls} />
                        <Calendar size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                      </div>
                    </Field>
                  </div>
                )}

                {/* Sélection artiste base + lieu prestation (lieu verrouillé en mode lié) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 pb-5 border-b border-slate-800">
                  <Field label="Artiste (base CRM)">
                    <select
                      value={selectedArtisteId}
                      onChange={(e) => setSelectedArtisteId(e.target.value)}
                      className={inputCls}
                    >
                      <option value="">— Sélectionner un artiste —</option>
                      {artistesDb.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.nom_scene}
                          {a.domaine_principal ? ` · ${a.domaine_principal}` : ''}
                          {a.lat && a.lng ? ' 📍' : ''}
                        </option>
                      ))}
                    </select>
                    {selectedArtiste && !selectedArtiste.lat && (
                      <p className="text-[10px] text-amber-400/80 mt-1">⚠ Adresse non géocodée — KM non calculé automatiquement</p>
                    )}
                  </Field>
                  <Field label={calcMode === 'lie' ? 'Lieu de la prestation (verrouillé)' : 'Lieu de la prestation'}>
                    {calcMode === 'lie' ? (
                      <input
                        value={lieuPrestation}
                        readOnly
                        className={inputCls + ' opacity-70 cursor-not-allowed'}
                      />
                    ) : (
                      <LieuAutocomplete
                        value={lieuPrestation}
                        onChange={setLieuPrestation}
                        onPick={(s) => {
                          setLieuPrestation(s.display_name);
                          setLieuCoords({ lat: parseFloat(s.lat), lon: parseFloat(s.lon) });
                        }}
                        hasCoords={!!lieuCoords}
                      />
                    )}
                  </Field>
                </div>

            {/* Top 5 artistes les plus proches du lieu */}
            {topProches.length > 0 && (
              <div className="mb-5 pb-5 border-b border-slate-800">
                <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <Compass size={11} strokeWidth={1.5} /> Top 5 artistes proches du lieu (vol d'oiseau)
                </div>
                <div className="flex flex-wrap gap-2">
                  {topProches.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setSelectedArtisteId(a.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${selectedArtisteId === a.id ? 'bg-amber-500/20 border-amber-400 text-amber-300' : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-amber-500/40'}`}
                    >
                      {a.nom_scene} <span className="text-slate-500">·</span> <span className="text-amber-400">{a.km} km</span> <span className="text-slate-500">· {formatDuree(a.minutes)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Artiste (libellé)">
                <input value={artistName} onChange={(e) => setArtistName(e.target.value)} className={inputCls} placeholder="Nom de l'artiste" />
              </Field>
              <Field label="Domaine">
                <select value={domainName} onChange={(e) => setDomainName(e.target.value)} className={inputCls}>
                  <option value="">—</option>
                  {data.domaines.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
              <Field label={`KM artiste → lieu${dureeTrajet ? ` (${dureeTrajet})` : ''}`}>
                <div className="relative">
                  <input value={km} onChange={(e) => setKm(e.target.value)} className={inputCls} />
                  {computingRoute && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-400 animate-spin" />}
                </div>
              </Field>
              <Field label="Tarif artiste HT — Main d'œuvre (€)">
                <input value={tarifHT} onChange={(e) => setTarifHT(e.target.value)} className={inputCls} />
              </Field>
              <Field label="Marge Cygnature (%)">
                <input value={marge} onChange={(e) => setMarge(e.target.value)} className={inputCls} />
              </Field>
              <Field label="Prop. HT client — saisie libre (€)">
                <input value={propTTC} onChange={(e) => setPropTTC(e.target.value)} className={inputCls} placeholder={String(Math.round(calc.suggestion))} />
              </Field>
            </div>

            {/* Options multiples (alignées Excel client) */}
            <div className="mt-6 pt-5 border-t border-slate-800">
              <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-3">Options HT (jusqu'à 4)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { lib: opt1Lib, setLib: setOpt1Lib, ht: opt1HT, setHt: setOpt1HT, n: 1 },
                  { lib: opt2Lib, setLib: setOpt2Lib, ht: opt2HT, setHt: setOpt2HT, n: 2 },
                  { lib: opt3Lib, setLib: setOpt3Lib, ht: opt3HT, setHt: setOpt3HT, n: 3 },
                  { lib: opt4Lib, setLib: setOpt4Lib, ht: opt4HT, setHt: setOpt4HT, n: 4 },
                ].map((o) => (
                  <div key={o.n} className="grid grid-cols-[1fr_120px] gap-2">
                    <input value={o.lib} onChange={(e) => o.setLib(e.target.value)} className={inputCls} placeholder={`Option ${o.n} — libellé`} />
                    <input value={o.ht} onChange={(e) => o.setHt(e.target.value)} className={inputCls} placeholder="HT €" />
                  </div>
                ))}
              </div>
              {calc.optionsHT > 0 && (
                <p className="text-[11px] text-amber-300 mt-2">Total options HT : <strong>{formatEUR(calc.optionsHT)}</strong></p>
              )}
            </div>

            {/* Frais VHR détaillés (alignés Excel client) */}
            <div className="mt-5 pt-5 border-t border-slate-800">
              <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-3">Frais VHR (Voyage / Hôtel / Restauration & Production)</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Frais VHR déplacement (€)">
                  <input value={vhrDepl} onChange={(e) => setVhrDepl(e.target.value)} className={inputCls} />
                </Field>
                <Field label="Frais VHR hôtel (€)">
                  <input value={vhrHotel} onChange={(e) => setVhrHotel(e.target.value)} className={inputCls} />
                </Field>
                <Field label="Frais VHR production (€)">
                  <input value={vhrProd} onChange={(e) => setVhrProd(e.target.value)} className={inputCls} />
                </Field>
              </div>
              <div className="mt-3">
                <Field label="Autres frais (€) — optionnel">
                  <input value={frais} onChange={(e) => setFrais(e.target.value)} className={inputCls} />
                </Field>
              </div>
              {calc.vhrTotal > 0 && (
                <p className="text-[11px] text-amber-300 mt-2">Total VHR : <strong>{formatEUR(calc.vhrTotal)}</strong></p>
              )}
            </div>
            {/* Liste des artistes ajoutés au calcul */}
            {lignes.length > 0 && (
              <div className="mt-6 pt-5 border-t border-slate-800">
                <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-3">
                  Artistes ajoutés au calcul ({lignes.length})
                </div>
                <div className="space-y-2">
                  {lignes.map((l) => (
                    <div key={l.id} className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-900/40 border border-slate-800 rounded-lg text-xs">
                      <div className="min-w-0 flex-1">
                        <div className="text-amber-300 font-medium truncate">{l.artiste_nom}</div>
                        <div className="text-slate-500 text-[10px]">
                          {l.domaine || '—'} · {formatEUR(l.tarif_ht + l.options_ht)} HT
                          {l.km != null ? ` · ${l.km} km` : ''}
                        </div>
                      </div>
                      <button onClick={() => handleRemoveLigne(l.id)} className="text-slate-500 hover:text-red-400" title="Retirer">
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-amber-300 mt-2">Total artistes ajoutés : <strong>{formatEUR(calc.lignesHT)}</strong></p>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={handleAddLigne} className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
                + Ajouter cet artiste au calcul
              </button>
              <button onClick={handleValider} className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
                <Check size={14} strokeWidth={1.5} /> Valider la prestation
              </button>
            </div>
              </>
            )}
          </Card>

          <Card className="p-6">
            <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-4">Résultat</div>
            <div className="space-y-3 text-sm">
              <Row label="Main d'œuvre HT" value={formatEUR(Number(tarifHT) || 0)} />
              {calc.optionsHT > 0 && <Row label="Options HT (cumul)" value={formatEUR(calc.optionsHT)} />}
              <Row label="Sous-total HT" value={formatEUR(calc.mainHT)} />
              <Row label="Artiste HT" value={formatEUR(calc.artisteHT)} />
              {calc.vhrTotal > 0 && <Row label="Frais VHR" value={formatEUR(calc.vhrTotal)} />}
              <Row label="Coût total HT" value={formatEUR(calc.coutTotal)} />
              <Row label="Suggestion client HT" value={formatEUR(calc.suggestion)} />
              <Row label="Prop. Cygnature HT (libre)" value={formatEUR(calc.clientHT)} accent />
              <Row label="Gain estimé" value={formatEUR(calc.gain)} />
              <Row label="Marge réelle" value={`${calc.margeReelle.toFixed(1)} %`} />
              <div className="pt-3 mt-2 border-t border-slate-800 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-400 text-xs uppercase tracking-widest">TVA appliquée</span>
                  <TvaSelect value={tva} onChange={(v) => setTva(String(v))} className="bg-slate-900 border border-slate-800 rounded-md px-2 py-1 text-sm text-slate-100" />
                </div>
                <Row label={`Total TVA (${tva.replace('.', ',')} %)`} value={formatEUR(calc.clientHT * (Number(tva) || 0) / 100)} />
                <Row label="Total TTC" value={formatEUR(calc.clientHT * (1 + (Number(tva) || 0) / 100))} accent />
              </div>
              {dureeTrajet && (
                <Row label="Durée trajet artiste" value={dureeTrajet} />
              )}
            </div>
          </Card>
        </div>
      )}

      {tab === 'recherche' && (
        <Card className="p-6">
          <h3 className="text-lg font-heading text-slate-200 mb-4 flex items-center gap-2"><Compass size={18} className="text-amber-400" strokeWidth={1.5} /> Recherche d'artistes par proximité</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <Field label="Lieu de l'événement">
              <LieuAutocomplete
                value={searchLieu}
                onChange={setSearchLieu}
                onPick={(s) => { setSearchLieu(s.display_name); setSearchCoords({ lat: parseFloat(s.lat), lon: parseFloat(s.lon) }); }}
                hasCoords={!!searchCoords}
              />
            </Field>
            <Field label="Date événement">
              <input type="date" value={searchDate} onChange={(e) => setSearchDate(e.target.value)} className={inputCls} />
            </Field>
            <Field label="Domaine (optionnel)">
              <select value={searchDomaine} onChange={(e) => setSearchDomaine(e.target.value)} className={inputCls}>
                <option value="">Tous les domaines</option>
                {data.domaines.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
          </div>
          <button onClick={runSearch} disabled={searching || !searchCoords} className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 rounded-lg text-sm font-medium flex items-center gap-2">
            {searching ? <Loader2 size={14} className="animate-spin" /> : <Compass size={14} strokeWidth={1.5} />}
            {searching ? 'Calcul des trajets…' : 'Lancer la recherche'}
          </button>

          {searchResults.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/80 text-[10px] text-slate-400 uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-3 text-left">Artiste</th>
                    <th className="px-3 py-3 text-left">Domaine</th>
                    <th className="px-3 py-3 text-left">Ville</th>
                    <th className="px-3 py-3 text-right">Distance</th>
                    <th className="px-3 py-3 text-right">Durée</th>
                    <th className="px-3 py-3 text-right">Tarif base</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((a) => (
                    <tr key={a.id} className="border-t border-slate-800/60 hover:bg-amber-500/5">
                      <td className="px-3 py-3 text-amber-300 font-medium text-xs">{a.nom_scene}</td>
                      <td className="px-3 py-3 text-slate-300 text-xs">{a.domaine_principal || '—'}</td>
                      <td className="px-3 py-3 text-slate-400 text-xs">{a.ville || '—'}</td>
                      <td className="px-3 py-3 text-right text-amber-400 text-xs font-medium">{a.km} km</td>
                      <td className="px-3 py-3 text-right text-slate-300 text-xs">{formatDuree(a.minutes)}</td>
                      <td className="px-3 py-3 text-right text-slate-300 text-xs">{a.tarif_base != null ? formatEUR(Number(a.tarif_base)) : '—'}</td>
                      <td className="px-3 py-3 text-right">
                        <button onClick={() => useArtisteForCalc(a)} className="px-2.5 py-1 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-300 rounded text-[11px]">
                          Utiliser
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'brouillons' && (
        <Card className="p-6">
          <h3 className="text-lg font-heading text-slate-200 mb-4 flex items-center gap-2"><FileText size={18} className="text-amber-400" strokeWidth={1.5} /> Brouillons de calcul</h3>
          {brouillons.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-12">Aucun brouillon. Utilisez le calculateur pour en créer.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/80 text-[10px] text-slate-400 uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-3 text-left">Créé le</th>
                    <th className="px-3 py-3 text-left">Client</th>
                    <th className="px-3 py-3 text-left">Date évt</th>
                    <th className="px-3 py-3 text-left">Artiste</th>
                    <th className="px-3 py-3 text-left">Lieu</th>
                    <th className="px-3 py-3 text-right">Km</th>
                    <th className="px-3 py-3 text-right">Prop. TTC</th>
                    <th className="px-3 py-3 text-left">Statut</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {brouillons.map((b) => (
                    <tr key={b.id} className="border-t border-slate-800/60 hover:bg-slate-800/30">
                      <td className="px-3 py-3 text-slate-400 text-xs">{formatDateFR(b.created_at)}</td>
                      <td className="px-3 py-3 text-slate-200 text-xs">{b.client_nom || <span className="text-slate-600 italic">—</span>}</td>
                      <td className="px-3 py-3 text-slate-400 text-xs">{b.date_event ? formatDateFR(b.date_event) : '—'}</td>
                      <td className="px-3 py-3 text-amber-300 text-xs">{b.artiste_nom}</td>
                      <td className="px-3 py-3 text-slate-400 text-xs max-w-[200px] truncate">{b.lieu || '—'}</td>
                      <td className="px-3 py-3 text-right text-slate-300 text-xs">{b.km ?? '—'}</td>
                      <td className="px-3 py-3 text-right text-amber-400 text-xs">{b.prop_ttc != null ? formatEUR(Number(b.prop_ttc)) : '—'}</td>
                      <td className="px-3 py-3 text-xs">
                        {b.prestation_id ? (
                          <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 text-[10px] border border-emerald-500/30">Converti</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded bg-slate-700/40 text-slate-400 text-[10px] border border-slate-700">Brouillon</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        {!b.prestation_id && (
                          <button onClick={() => handleConvertirBrouillon(b)} className="px-2.5 py-1 mr-1 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-300 rounded text-[11px] inline-flex items-center gap-1">
                            <Send size={11} strokeWidth={1.5} /> Convertir
                          </button>
                        )}
                        <button onClick={() => handleDeleteBrouillon(b.id)} className="px-2 py-1 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 rounded text-[11px]">
                          <Trash2 size={11} strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function SortableTh({ label, k, sortKey, sortDir, onSort, align = 'left' }: { label: string; k: SortKey; sortKey: SortKey; sortDir: SortDir; onSort: (k: SortKey) => void; align?: 'left' | 'right' }) {
  const active = sortKey === k;
  const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-3 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1.5 hover:text-amber-300 transition-colors ${active ? 'text-amber-400' : ''}`}
      >
        {label}
        <Icon size={11} strokeWidth={2} />
      </button>
    </th>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-800 pb-3 last:border-b-0">
      <span className="text-slate-400">{label}</span>
      <span className={accent ? 'text-emerald-400 font-medium' : 'text-slate-100'}>{value}</span>
    </div>
  );
}

function LieuAutocomplete({
  value,
  onChange,
  onPick,
  hasCoords,
}: {
  value: string;
  onChange: (v: string) => void;
  onPick: (s: NominatimSuggestion) => void;
  hasCoords: boolean;
}) {
  const [suggestions, setSuggestions] = useState<NominatimSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Debounce stable + dédoublonnage des résultats Nominatim
  useEffect(() => {
    if (value.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    let cancelled = false;
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const raw = await searchAddress(value);
        if (cancelled) return;
        const seen = new Set<string>();
        const dedup = raw.filter((s) => {
          const key = `${s.display_name}|${s.lat}|${s.lon}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setSuggestions(dedup);
        setOpen(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [value]);

  const handleChange = (v: string) => onChange(v);

  return (
    <div className="relative">
      <div className="relative">
        <input
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Ville, commune, lieu…"
          className={inputCls + ' pr-8'}
          autoComplete="off"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          {loading ? (
            <Loader2 size={14} className="text-amber-400 animate-spin" />
          ) : hasCoords ? (
            <Check size={14} className="text-emerald-400" strokeWidth={2} />
          ) : (
            <MapPin size={14} className="text-slate-500" strokeWidth={1.5} />
          )}
        </div>
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-slate-950 border border-slate-800 rounded-lg shadow-xl">
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setOpen(false); setSuggestions([]); onPick(s); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/80 border-b border-slate-800/50 last:border-0 flex items-start gap-2"
            >
              <MapPin size={12} className="text-amber-400 mt-1 shrink-0" strokeWidth={1.5} />
              <span className="line-clamp-2">{s.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-amber-500/50';

function ClientCalcPicker({
  value,
  onChange,
  prestations,
  onPickPrestation,
}: {
  value: string;
  onChange: (v: string) => void;
  prestations: import('../types').Prestation[];
  onPickPrestation: (p: import('../types').Prestation) => void;
}) {
  const [open, setOpen] = useState(false);
  const [freshPrest, setFreshPrest] = useState<import('../types').Prestation[]>([]);
  const ref = useMemo(() => ({ current: null as HTMLDivElement | null }), []);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref]);

  // Charge les prestations directement depuis Supabase au focus (évite le cache périmé du store)
  const fetchFresh = useCallback(async () => {
    const { data: rows } = await supabase
      .from('prestations')
      .select('id, numero, client_nom, date_event, lieu, libelle, client_id')
      .is('deleted_at', null)
      .eq('is_demo', demoFilterValue())
      .order('date_event', { ascending: false })
      .limit(300);
    const mapped = (rows || []).map((r: any) => ({
      id: r.numero || r.id,
      client: r.client_nom || '',
      dateEvent: r.date_event || '',
      lieu: r.lieu || '',
      libelle: r.libelle || '',
      clientId: r.client_id,
    })) as any[];
    setFreshPrest(mapped);
  }, []);

  const source = freshPrest.length ? freshPrest : prestations;

  const suggestions = useMemo(() => {
    const q = value.trim().toLowerCase();
    const seen = new Set<string>();
    return source
      .filter((p: any) => p.client && p.client.trim())
      .filter((p: any) => !q || p.client.toLowerCase().includes(q) || (p.lieu || '').toLowerCase().includes(q))
      .sort((a: any, b: any) => (b.dateEvent || '').localeCompare(a.dateEvent || ''))
      .filter((p: any) => {
        const key = `${p.client}|${p.dateEvent}|${p.lieu}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 15);
  }, [value, source]);

  return (
    <div ref={ref} className="relative">
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); fetchFresh(); }}
        placeholder="Rechercher un client existant ou saisir…"
        className={inputCls}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-slate-950 border border-slate-800 rounded-lg shadow-xl">
          <div className="px-3 py-2 text-[10px] text-slate-500 uppercase tracking-widest border-b border-slate-800 bg-slate-900/40">
            {value.trim() ? `Suggestions (${suggestions.length})` : 'Clients récents'}
          </div>
          {suggestions.map((p) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPickPrestation(p); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-amber-500/10 hover:text-amber-300 border-b border-slate-800/50 last:border-0"
              title="Pré-remplit date et lieu"
            >
              <div className="truncate font-medium">{p.client}</div>
              <div className="text-[10px] text-slate-500 truncate">
                {p.dateEvent ? formatDateFR(p.dateEvent) : '—'}
                {p.lieu ? ` · ${p.lieu}` : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- PrestationPicker : sélecteur dédié de prestation existante ---------- */
function PrestationPicker({
  prestations,
  onPick,
}: {
  prestations: import('../types').Prestation[];
  onPick: (p: import('../types').Prestation) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useMemo(() => ({ current: null as HTMLDivElement | null }), []);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return prestations
      .filter((p) => p.client && p.client.trim())
      .filter((p) => {
        if (!q) return true;
        return (
          p.client.toLowerCase().includes(q) ||
          (p.lieu || '').toLowerCase().includes(q) ||
          (p.libelle || '').toLowerCase().includes(q) ||
          (p.dateEvent || '').includes(q)
        );
      })
      .sort((a, b) => (b.dateEvent || '').localeCompare(a.dateEvent || ''))
      .slice(0, 30);
  }, [prestations, query]);

  return (
    <div ref={ref} className="relative">
      <input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Rechercher par client, date ou lieu…"
        className={inputCls}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-80 overflow-y-auto bg-slate-950 border border-slate-800 rounded-lg shadow-xl">
          <div className="px-3 py-2 text-[10px] text-slate-500 uppercase tracking-widest border-b border-slate-800 bg-slate-900/40">
            {filtered.length} prestation(s)
          </div>
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-slate-500">Aucune prestation trouvée</div>
          )}
          {filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(p); setOpen(false); setQuery(''); }}
              className="w-full text-left px-3 py-2.5 text-sm text-slate-200 hover:bg-amber-500/10 hover:text-amber-300 border-b border-slate-800/50 last:border-0"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{p.client}</span>
                <span className="text-[10px] text-amber-400 shrink-0">{p.dateEvent ? formatDateFR(p.dateEvent) : 'Sans date'}</span>
              </div>
              <div className="text-[10px] text-slate-500 truncate mt-0.5">
                {p.libelle ? `${p.libelle} · ` : ''}{p.lieu || 'Lieu non renseigné'}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
